import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import MergeIcon from '@mui/icons-material/MergeType';
import { T_KEYS } from '../../../constants';

const EmptyStateContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '64px 24px',
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  border: '1px solid #e6e6e6',
  textAlign: 'center',
  minHeight: '400px',
});

const IconContainer = styled(Box)({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: '#f3f0ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
});

const Title = styled(Typography)({
  fontSize: '20px',
  fontWeight: 700,
  color: '#262626',
  marginBottom: '12px',
});

const Description = styled(Typography)({
  fontSize: '16px',
  color: '#666666',
  marginBottom: '8px',
  maxWidth: '500px',
  lineHeight: '24px',
});

const HelperText = styled(Typography)({
  fontSize: '14px',
  color: '#999999',
  marginBottom: '32px',
  maxWidth: '500px',
  lineHeight: '20px',
});

const StartButton = styled(Button)({
  backgroundColor: '#5b3fff',
  color: '#ffffff',
  padding: '12px 32px',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: '#4a2fe8',
  },
});

interface EmptyStateProps {
  onStartMapping: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = memo(({ onStartMapping }) => {
  const { t } = useTranslation();

  return (
    <EmptyStateContainer>
      <IconContainer>
        <MergeIcon sx={{ fontSize: 40, color: '#5b3fff' }} />
      </IconContainer>
      <Title>
        {t(T_KEYS.UNITS_MAPPING_EMPTY_STATE_TITLE)}
      </Title>
      <Description>
        {t(T_KEYS.UNITS_MAPPING_EMPTY_STATE_DESCRIPTION)}
      </Description>
      <HelperText>
        {t(T_KEYS.UNITS_MAPPING_EMPTY_STATE_HELPER)}
      </HelperText>
      <StartButton onClick={onStartMapping}>
        {t(T_KEYS.UNITS_MAPPING_START_MAPPING)}
      </StartButton>
    </EmptyStateContainer>
  );
});

EmptyState.displayName = 'EmptyState';

export default EmptyState;
